import dataPoints from './data-points/index.js'
import {compareValues, formatRow} from './lib/util.js'
import {FIELD_TYPES_MAP, FIELD_TYPES_ENUM} from './lib/constants.js'
import {PROFILE_TEMPLATES, PROFILE_TEMPLATE_IDS} from './profile-templates/index.js'
import {format} from 'date-fns'

export {FIELD_TYPES_MAP, FIELD_TYPES_ENUM, PROFILE_TEMPLATES, PROFILE_TEMPLATE_IDS}

// Data point types whose value is locked once set: the caller flags the answer as
// non-editable and any attempt to change it is rejected. SYSTEM values are assigned
// by the platform, so they are protected the same way GLOBAL ones are.
const LOCKED_WHEN_NOT_EDITABLE_TYPES = ['GLOBAL', 'SYSTEM']

// An option's follow-up is the text the responder typed under it (usually "Other").
// Rendered as `Label — text`, the same string the responses page shows; "Other" alone
// says nothing about the answer.
const withFollowUp = (label, followUp) => {
  // Some follow-up schemas allow a number as well as a string.
  const text = followUp?.value == null ? '' : String(followUp.value).trim()
  if (!text) return label
  return label ? `${label} — ${text}` : text
}

class DataPointsLib {
  constructor() {}

  initializeDataPoints() {
    return {
      validate: this.validate,
      transformProfileAnswer: this.transformProfileAnswer,
      transformAnswer: this.transformAnswer,
      getDataPointById: this.getDataPointById,
      transformAnswerToHumanReadable: this.transformAnswerToHumanReadable,
    }
  }

  validate = async (dataPointId, value, {meta, validatorFn, oldValue, isEditable}) => {
    if (value) {
      const camelCasedDataPointId = dataPointId.replace(/_([a-z])/g, g => g[1].toUpperCase())
      const dataPoint = dataPoints[camelCasedDataPointId]
      if (!dataPoint) {
        throw new Error(`[DataPointsLIB] Error: Data point ${(dataPointId, camelCasedDataPointId)} not found.`)
      }

      const {
        dataPointDefinition: {type, fieldTypeId},
      } = dataPoint

      if (
        LOCKED_WHEN_NOT_EDITABLE_TYPES.includes(type) &&
        fieldTypeId !== 'ASSET' &&
        isEditable != null &&
        !isEditable
      ) {
        const isSameValue = compareValues(value.value, oldValue.value)
        if (!isSameValue) {
          throw new Error(
            `[DataPointsLIB] Error: Data point ${(dataPointId, camelCasedDataPointId)} with value ${JSON.stringify(value)} and old value ${JSON.stringify(oldValue)} cannot be updated`
          )
        }
      }

      await dataPoint.validateSchema(value, {meta, validatorFn})

      return value
    }
  }

  transformProfileAnswer = ({profileAnswer}) => {
    let transformedProfile = {}
    for (const dataPointId in profileAnswer) {
      const camelCasedDataPointId = dataPointId.replace(/_([a-z])/g, g => g[1].toUpperCase())
      const dataPoint = dataPoints[camelCasedDataPointId]
      if (!dataPoint) {
        throw new Error(`[DataPointsLIB] Error: Data point ${dataPointId} not found.`)
      }

      const transformedAnswer = this.transformAnswer({
        dataPointId,
        answer: profileAnswer[dataPointId],
        meta: profileAnswer[dataPointId].meta,
      })

      transformedProfile = {...transformedProfile, ...transformedAnswer}
    }

    return transformedProfile
  }

  transformAnswer = ({dataPointId, answer, meta}) => {
    const camelCasedDataPointId = dataPointId.replace(/_([a-z])/g, g => g[1].toUpperCase())
    const dataPoint = dataPoints[camelCasedDataPointId]

    if (!dataPoint) {
      throw new Error(`[DataPointsLIB] Error: Data point ${dataPointId} not found.`)
    }
    if (!answer) return {[camelCasedDataPointId]: null}

    const result = dataPoint.transformAnswer({[dataPointId]: answer}, {meta})

    return result
  }

  getDataPointById = dataPointId => {
    const camelCasedDataPointId = dataPointId.replace(/_([a-z])/g, g => g[1].toUpperCase())
    const dataPoint = dataPoints[camelCasedDataPointId]

    if (!dataPoint) {
      throw new Error(`[DataPointsLIB] Error: Data point ${dataPointId} not found.`)
    }

    return dataPoint
  }

  transformAnswerToHumanReadable = async (dataPointId, answer, {meta, validatorFn}) => {
    const camelCasedDataPointId = dataPointId.replace(/_([a-z])/g, g => g[1].toUpperCase())
    const dataPoint = dataPoints[camelCasedDataPointId]

    if (!dataPoint) {
      throw new Error(`[DataPointsLIB] Error: Data point ${dataPointId} not found.`)
    }

    await dataPoint.validateSchema(answer, {meta, validatorFn})

    let humanReadableAnswer = ''

    switch (dataPoint.dataPointDefinition.fieldTypeId) {
      case FIELD_TYPES_MAP.ADDRESS:
        humanReadableAnswer = `[${answer.value.geometry.coordinates.join(', ')}]`
        break

      case FIELD_TYPES_MAP.ASSET:
        humanReadableAnswer = `${answer.body.name} - ${answer.body.id}`
        break

      case FIELD_TYPES_MAP.BOOLEAN:
        humanReadableAnswer = answer.value ? 'true' : 'false'
        break

      case FIELD_TYPES_MAP.CHECK_BOX:
        humanReadableAnswer =
          meta?.options?.length && Array.isArray(answer.value)
            ? answer.value
                .map(selectedOption =>
                  withFollowUp(
                    meta.options.find(option => option.key === selectedOption.key && selectedOption.isChecked === true)
                      ?.text,
                    selectedOption.followUp
                  )
                )
                .join(', ')
            : ''
        break

      case FIELD_TYPES_MAP.DATE:
        humanReadableAnswer = answer.value ? format(new Date(answer.value), 'PPP') : answer.value
        break

      case FIELD_TYPES_MAP.MULTI_SELECT:
        // Multiselect can either be ENUM (supply chain tier) or an array of objects (everything else)
        if (meta?.options?.length) {
          humanReadableAnswer = answer.value
            .map(selectedOption => {
              const key = typeof selectedOption === 'string' ? selectedOption : selectedOption?.id
              return meta.options.find(option => option.key === key)?.text
            })
            .join(', ')
        } else {
          humanReadableAnswer = ''
        }

        break

      case FIELD_TYPES_MAP.MULTIPLE_PHOTO_UPLOAD:
        humanReadableAnswer = Array.isArray(answer.value) ? answer.value.map(photo => ({photo})) : ''
        break

      case FIELD_TYPES_MAP.NUMBER_DYNAMIC_CURRENCY_WITH_YEAR:
        humanReadableAnswer = `${answer.year}: ${answer.value} ${answer.unit}`
        break

      case FIELD_TYPES_MAP.NUMBER_DYNAMIC_UOM_WITH_YEAR:
        humanReadableAnswer = `${answer.year}: ${answer.value} ${answer.unit}`
        break

      case FIELD_TYPES_MAP.NUMBER_DYNAMIC_UOM:
        humanReadableAnswer = `${answer.value} ${answer.unit}`
        break

      case FIELD_TYPES_MAP.NUMBER_UOM:
        humanReadableAnswer = `${answer.value} ${answer.unit}`
        break

      case FIELD_TYPES_MAP.PASSWORD:
        // Defense-in-depth: never reveal ciphertext (or any value) in human-readable form
        humanReadableAnswer = '••••••••'
        break

      case FIELD_TYPES_MAP.SELECT:
        humanReadableAnswer = meta?.options?.length
          ? meta.options.find(option => option.key === answer.value)?.text
          : ''
        break

      // One level deeper than SELECT: `{value: {value: key, followUp: {value: text} | null}}`.
      // Without its own case this fell to `default` and returned the inner object.
      case FIELD_TYPES_MAP.SELECT_WITH_FOLLOW_UP:
        humanReadableAnswer = meta?.options?.length
          ? withFollowUp(
              meta.options.find(option => option.key === answer.value?.value)?.text,
              answer.value?.followUp
            )
          : ''
        break

      case FIELD_TYPES_MAP.TABLE:
        humanReadableAnswer = Array.isArray(answer.value)
          ? answer.value.map(formatRow).join('\n')
          : formatRow(answer.value)
        break

      case FIELD_TYPES_MAP.VIDEO_UPLOAD:
        humanReadableAnswer = `${answer.value.url}`
        break

      case FIELD_TYPES_MAP.YES_NO:
        humanReadableAnswer = `${answer.value}`
        break

      // Applies for LONG_TEXT, NUMBER, SHORT_TEXT, STRING
      default:
        humanReadableAnswer = answer.value
        break
    }

    return humanReadableAnswer
  }
}

export default DataPointsLib
